// STEP TIKTOK-ACCOUNTS-REVIEW-DEMO-1 · Comment Moderation Prototype UI.
// 목적: TikTok Accounts API Access Application Form 심사 영상용 최소 화면.
// Prototype 데이터만 사용 · 실제 TikTok API 호출 0.
// 심사 흐름: Authorized TikTok Account → My TikTok Posts → Comments → Reply.
//
// 향후 Instagram+TikTok 통합 Comment 함 확장 가능하도록 componet schema 에
// `platform` 필드 포함.  단, 공통 엔진 리팩터링은 본 STEP 범위 외.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Music, ShieldCheck, MessageSquare, CornerDownRight,
         RefreshCcw, Send } from 'lucide-react';

const PrototypeBanner = ({ pos = 'top' }) => (
  <div
    data-testid={`tt-review-demo-banner-${pos}`}
    className="w-full bg-amber-50 border border-amber-300 text-amber-900 text-xs
               rounded-md px-3 py-2 flex items-center gap-2 font-semibold"
  >
    <ShieldCheck className="w-4 h-4" />
    Prototype for TikTok Accounts API review — fixture data only,
    no live TikTok API calls.
  </div>
);

const AuthorizedAccountCard = ({ account }) => {
  if (!account) return null;
  return (
    <Card data-testid="tt-review-demo-authorized-account"
          className="p-4 bg-black text-white">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center
                         justify-center shrink-0">
          <Music className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-white/60">
              Authorized TikTok Account
            </span>
            <Badge data-testid="tt-review-demo-account-source"
                   className={`text-[10px] ${
                     account.source === 'live_oauth'
                       ? 'bg-emerald-500 text-black'
                       : 'bg-amber-400 text-black'}`}>
              {account.source === 'live_oauth'
                ? 'Live OAuth connection'
                : 'Prototype account (simulated)'}
            </Badge>
          </div>
          <div data-testid="tt-review-demo-account-name"
               className="text-xl font-black mt-1">
            {account.display_name}
          </div>
          <div className="text-[11px] text-white/60 mt-0.5">
            Authorized at: {account.authorized_at || '-'}
            {account.open_id_masked ? ` · open_id: ${account.open_id_masked}` : ''}
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(account.authorized_scopes || []).map((s) => (
              <Badge key={s} data-testid={`tt-review-demo-scope-${s}`}
                     className="bg-white/10 border border-white/20 text-[10px]">
                {s}
              </Badge>
            ))}
          </div>
          <p className="text-[11px] text-white/70 mt-3 leading-relaxed">
            {account.notice}
          </p>
        </div>
      </div>
    </Card>
  );
};

const CommentReply = ({ reply }) => (
  <div data-testid={`tt-review-demo-reply-${reply.reply_id}`}
       className="flex items-start gap-2 mt-2 pl-6 border-l-2 border-emerald-400">
    <CornerDownRight className="w-3.5 h-3.5 text-emerald-600 mt-1 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-emerald-700">
          {reply.author_display || reply.author}
        </span>
        <Badge className="bg-emerald-600 text-white text-[9px]">
          Seller Reply
        </Badge>
        <span className="text-[10px] text-gray-400">
          {new Date(reply.created_at).toLocaleString('ko-KR')}
        </span>
      </div>
      <div className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap break-words">
        {reply.text}
      </div>
    </div>
  </div>
);

const CommentItem = ({ comment, onReply, isReplying, onReplyingChange }) => {
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      await onReply(comment.comment_id, text);
      setDraft('');
      onReplyingChange(null);
    } finally {
      setSubmitting(false);
    }
  }, [draft, comment.comment_id, onReply, onReplyingChange]);

  return (
    <div data-testid={`tt-review-demo-comment-${comment.comment_id}`}
         className="p-3 rounded-lg bg-gray-50 border border-gray-200">
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center
                         justify-center text-xs font-bold text-pink-700 shrink-0">
          {(comment.author_display || comment.author || '?').slice(0, 1)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900">
              {comment.author_display || comment.author}
            </span>
            <span className="text-xs text-gray-500">{comment.author}</span>
            <Badge className="bg-gray-200 text-gray-700 text-[9px]">
              {comment.platform}
            </Badge>
            <span className="text-[10px] text-gray-400">
              {new Date(comment.created_at).toLocaleString('ko-KR')}
            </span>
          </div>
          <div className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words">
            {comment.text}
          </div>
          {(comment.replies || []).map((r) => (
            <CommentReply key={r.reply_id} reply={r} />
          ))}
          {isReplying ? (
            <div data-testid={`tt-review-demo-reply-form-${comment.comment_id}`}
                 className="mt-3 space-y-2">
              <Textarea
                data-testid={`tt-review-demo-reply-input-${comment.comment_id}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="Write a reply to this comment…"
                className="text-sm"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-400">
                  {draft.length}/500
                </span>
                <div className="flex gap-2">
                  <Button
                    data-testid={`tt-review-demo-reply-cancel-${comment.comment_id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => { setDraft(''); onReplyingChange(null); }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid={`tt-review-demo-reply-submit-${comment.comment_id}`}
                    size="sm"
                    onClick={handleSubmit}
                    disabled={submitting || !draft.trim()}
                    className="bg-black text-white hover:bg-gray-800"
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />
                    {submitting ? 'Sending…' : 'Reply'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button
              data-testid={`tt-review-demo-reply-open-${comment.comment_id}`}
              variant="outline"
              size="sm"
              className="mt-2 text-xs"
              onClick={() => onReplyingChange(comment.comment_id)}
            >
              <MessageSquare className="w-3.5 h-3.5 mr-1" />
              Reply
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function SellerTikTokReviewDemo() {
  const [snapshot, setSnapshot] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [replyingCommentId, setReplyingCommentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/seller/tiktok-review-demo/snapshot');
      setSnapshot(data);
      if (!selectedPostId && data.posts?.length) {
        setSelectedPostId(data.posts[0].post_id);
      }
    } catch (e) {
      toast.error('Failed to load TikTok prototype snapshot');
    } finally {
      setLoading(false);
    }
  }, [selectedPostId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectedPost = useMemo(() => {
    if (!snapshot?.posts) return null;
    return snapshot.posts.find((p) => p.post_id === selectedPostId) || snapshot.posts[0];
  }, [snapshot, selectedPostId]);

  const handleReply = useCallback(async (commentId, text) => {
    try {
      await api.post('/seller/tiktok-review-demo/reply', {
        post_id:    selectedPost?.post_id,
        comment_id: commentId,
        reply_text: text,
      });
      toast.success('Reply posted (prototype — not sent to TikTok)');
      await load();
    } catch (e) {
      toast.error('Reply failed · check console');
    }
  }, [selectedPost, load]);

  const handleReset = useCallback(async () => {
    setResetting(true);
    try {
      await api.post('/seller/tiktok-review-demo/reset', {});
      toast.success('Prototype replies cleared');
      setReplyingCommentId(null);
      await load();
    } catch (e) {
      toast.error('Reset failed');
    } finally {
      setResetting(false);
    }
  }, [load]);

  return (
    <div data-testid="tt-review-demo-page"
         className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      <PrototypeBanner pos="top" />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black tracking-tight"
            data-testid="tt-review-demo-title">
          TikTok Comment Moderation · Review Demo
        </h1>
        <Button
          data-testid="tt-review-demo-reset-btn"
          variant="outline"
          size="sm"
          onClick={handleReset}
          disabled={resetting}
        >
          <RefreshCcw className="w-3.5 h-3.5 mr-1" />
          {resetting ? 'Resetting…' : 'Reset replies'}
        </Button>
      </div>

      {loading && (
        <div data-testid="tt-review-demo-loading"
             className="p-10 text-center text-sm text-gray-500">
          Loading prototype snapshot…
        </div>
      )}

      {!loading && snapshot && (
        <>
          <AuthorizedAccountCard account={snapshot.authorized_account} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card data-testid="tt-review-demo-posts-list"
                  className="p-3 lg:col-span-1 space-y-2">
              <div className="text-sm font-bold text-gray-900 flex items-center gap-1">
                <Music className="w-4 h-4" />
                My TikTok Posts
                <Badge className="bg-gray-200 text-gray-700 text-[9px] ml-1">
                  {snapshot.posts?.length || 0}
                </Badge>
              </div>
              {(snapshot.posts || []).map((p) => {
                const active = p.post_id === selectedPost?.post_id;
                return (
                  <button
                    key={p.post_id}
                    data-testid={`tt-review-demo-post-${p.post_id}`}
                    onClick={() => { setSelectedPostId(p.post_id); setReplyingCommentId(null); }}
                    className={`w-full text-left p-2 rounded-md border transition-colors
                                 ${active
                                   ? 'bg-black text-white border-black'
                                   : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{p.thumbnail_emoji}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold truncate">{p.title}</div>
                        <div className={`text-[10px] ${active ? 'text-white/70' : 'text-gray-500'}`}>
                          {new Date(p.published_at).toLocaleDateString('ko-KR')}
                          {' · '}💬 {p.comments_count}
                          {' · '}❤ {p.like_count.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </Card>

            <Card data-testid="tt-review-demo-comments-list"
                  className="p-4 lg:col-span-2 space-y-3">
              {selectedPost && (
                <>
                  <div>
                    <div className="text-xs text-gray-500">Selected Post</div>
                    <div className="text-base font-black text-gray-900">
                      <span className="mr-2">{selectedPost.thumbnail_emoji}</span>
                      {selectedPost.title}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      {new Date(selectedPost.published_at).toLocaleString('ko-KR')}
                      {' · '}views {selectedPost.view_count.toLocaleString()}
                      {' · '}likes {selectedPost.like_count.toLocaleString()}
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="text-sm font-bold text-gray-900 mb-2
                                     flex items-center gap-1">
                      <MessageSquare className="w-4 h-4" />
                      Comments
                      <Badge className="bg-gray-200 text-gray-700 text-[9px] ml-1"
                             data-testid="tt-review-demo-comments-count">
                        {selectedPost.comments?.length || 0}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {(selectedPost.comments || []).map((c) => (
                        <CommentItem
                          key={c.comment_id}
                          comment={c}
                          onReply={handleReply}
                          isReplying={replyingCommentId === c.comment_id}
                          onReplyingChange={setReplyingCommentId}
                        />
                      ))}
                      {(!selectedPost.comments || selectedPost.comments.length === 0) && (
                        <div className="text-xs text-gray-400 italic py-6 text-center">
                          No comments on this post yet.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>

          <div className="text-[11px] text-gray-500 italic text-center">
            Fixture version: {snapshot.fixture_version}
            {' · '}Platform: {snapshot.platform}
            {' · '}In-memory replies reset on backend restart.
          </div>
        </>
      )}

      <PrototypeBanner pos="bottom" />
    </div>
  );
}
